// DashboardHome.jsx
import React, { useState, useEffect } from 'react';
import { 
    Users,
    DollarSign,
    TrendingUp,
    ArrowUpRight,
    ArrowDownRight,
    CreditCard,
    Building2,
    Activity,
    StarHalf,
    Rocket,
    Siren,
    LandPlot,
    ChartSpline,
    Coins
} from 'lucide-react';
import {
    LineChart,
    Line,
    AreaChart,
    Area,
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
    PieChart,
    Pie,
    Cell,
    Legend,
    ComposedChart
} from 'recharts';
import { formatCurrency, formatNumber, formatDateIntl, formatWithStyle, formatInterestFra } from "../../util/currency_lookup";
import { Loader } from '../../components/loader';
import './DashboardHome.css';
import {Card, CardContent, StatCard} from "../../components/card-components";
import CurrencySelector from '../../components/CurrencySelector';
import {PortfolioChart} from '../../components/charts/PortfolioChart';

const CustomerHome = () => {
    const [loading, setLoading] = useState({
        overall: true,
        fundsPosition: true,
        overtime: true,
    });
    const [error, setError] = useState(null);
    const [overall, setOverall] = useState(null);
    const [selectedCurrency, setSelectedCurrency] = useState('GBP');
    const [selectedInterval, setSelectedInterval] = useState('14');
    const [fundPosition, setFundPosition] = useState([]);
    const [overtimePosition, setOvertimePosition] = useState([]);

    useEffect(() => {
        fetchOverallPosition(selectedCurrency);
        fetchFundsPosition(selectedCurrency);
        fetchEarningOverTime(selectedCurrency);
    }, [selectedCurrency]);


    const fetchOverallPosition = async (targetCurrency) => {
        try {
            const token = localStorage.getItem('token');
            const baseUrl = process.env.REACT_APP_API_URL;

            const overallPositionResponse = await fetch(
                `${baseUrl}/api/v1/position/user-overall/${targetCurrency}`,
                {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    }
                }
            );
            if (!overallPositionResponse.ok) {
                setError("error loading data");
                setLoading(prev => ({ ...prev, overall: false }));
                return;
            }
            const overallPositionData = await overallPositionResponse.json();
            setOverall(overallPositionData);
            setLoading(prev => ({ ...prev, overall: false }));
        } catch(err) {
            setError("error loading data");
            setLoading(prev => ({ ...prev, overall: false }));
        }
    };

    const fetchFundsPosition = async (targetCurrency) => {
        try {
            const token = localStorage.getItem('token');
            const baseUrl = process.env.REACT_APP_API_URL;

            const fundPositionResponse = await fetch(
                `${baseUrl}/api/v1/position/user-by-fund/${targetCurrency}`,
                {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    }
                }
            );
            if (!fundPositionResponse.ok) {
                setError("error loading data");
                setLoading(prev => ({ ...prev, fundsPosition: false }));
                return;
            }
            const fundPositionData = await fundPositionResponse.json();
            setFundPosition(fundPositionData);
            setLoading(prev => ({ ...prev, fundsPosition: false }));
        } catch(err) {
            setError("error loading data");
            setLoading(prev => ({ ...prev, fundsPosition: false }));
        }
    };

    const fetchEarningOverTime = async (targetCurrency) => {
        try {
            const token = localStorage.getItem('token');
            const baseUrl = process.env.REACT_APP_API_URL;

            const overtimeResponse = await fetch(
                `${baseUrl}/api/v1/position/user-overtime/${targetCurrency}`,
                {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    }
                }
            );
            if (!overtimeResponse.ok) {
                setError("error loading data");
                setLoading(prev => ({ ...prev, overtime: false }));
                return;
            }
            const overtimeData = await overtimeResponse.json();
            setOvertimePosition(overtimeData);
            setLoading(prev => ({ ...prev, overtime: false }));
        } catch(err) {
            setError("error loading data");
            setLoading(prev => ({ ...prev, overtime: false }));
        }
    };

    const isLoading = Object.values(loading).some(status => status);

    if (isLoading) {
        return <Loader loading={true} />;
    }

    if (error) {
        return <div className="error">{error}</div>;
    }

    const COLORS = ['#6366f1', '#10b981', '#f59e0b', '#ef4444'];

    const handleCurrencyChange = (currency) => {
        setSelectedCurrency(currency);
    };

    return (
            <div className="dashboard-home">
            {/* Stats Grid */}

            <div className="dashboard-header">
                <h1>Dashboard</h1>
                <CurrencySelector 
                    selectedCurrency={selectedCurrency}
                    onCurrencyChange={handleCurrencyChange}
                />
            </div>

            <div className="dashboard-stats-grid">
                <div className="dashboard-stat-card">
                    <div className="stat-icon">
                        <Coins />
                    </div>
                    <div className="stat-content">
                        <div className="dashboard-stat-title">Total Shares</div>
                        <div className="dashboard-stat-value">
                            {formatNumber(overall.total_current_shares, selectedCurrency)}
                        </div>
                    </div>
                </div>

                <div className="dashboard-stat-card">
                    <div className="stat-icon">
                        <ChartSpline color='red'/>
                    </div>
                    <div className="stat-content">
                        <div className="dashboard-stat-title">Total Invested</div>
                        <div className="dashboard-stat-value">{formatCurrency(overall.total_invested, selectedCurrency)}</div>
                    </div>
                </div>

                <div className="dashboard-stat-card">
                    <div className="stat-icon">
                        <LandPlot />
                    </div>
                    <div className="stat-content">
                        <div className="dashboard-stat-title">Total Interest Earned</div>
                        <div className="dashboard-stat-value">{formatCurrency(overall.total_interest_earned, selectedCurrency)}</div>
                    </div>
                </div>
            </div>

            {/* Charts Grid */}
            <div className="charts-grid">
                {/* Investment Trends */}
                <div className="chart-card large">
                    <div className="chart-header">
                        <h3>Investment Trends</h3>
                    </div>
                    <div className="chart-content">
                        <PortfolioChart 
                            overtimePosition={overtimePosition}
                            selectedCurrency={selectedCurrency} />
                    </div>
                </div>
            </div>

            <div className="transactions-section">
                <div className="stats-header">
                    <h2 className="stats-title">Position</h2>
                </div>
                <Card>
                    <div className="tabs-header">
                    <div className="tabs">
                     </div>
                    </div>
                    <div className="table-container">
                    <table>
                        <thead>
                        <tr>
                            {/* <th>Transaction date</th> */}
                            <th>Fund Name</th>
                            <th>Currency</th>
                            <th>Days Invested</th>
                            <th>Shares</th>
                            <th>Invested</th>
                            <th>Withdrawn</th>
                            <th>Interest</th>
                            <th>Rate</th>
                        </tr>
                        </thead>
                        <tbody>
                        {fundPosition.map((transaction, index) => (
                        <tr key={index}>
                            {/* <td>{transaction.fund_manager_id}</td> */}
                            <td>
                                <div className="stacked-text">
                                  <span>{transaction.fund_name}</span>
                                  {/* <span>{transaction.user_last_name}</span> */}
                                </div>
                            </td>
                            <td>
                                <span className="highlight-underline">
                                  {transaction.currency_code}
                                </span>
                            </td>
                            <td>
                              <span className="my-span">
                              {transaction.days_invested}
                              </span>
                            </td>
                            <td>{formatNumber(transaction.current_shares)}</td>
                            <td>
                                {formatWithStyle(transaction.total_invested_original, 'compact', transaction.currency_code)}
                            </td>
                            <td>
                                {formatWithStyle(transaction.total_withdrawn_original, 'compact', transaction.currency_code)}
                            </td>
                            <td>{formatWithStyle(transaction.total_interest_earned_original,'compact', transaction.currency_code)}</td>
                            <td>{formatInterestFra(transaction.avg_annual_rate_pct)}</td>
                            {/* <td>{transaction.receiveAmount}</td>
                            <td><span className={`status ${transaction.status.toLowerCase()}`}>{transaction.status}</span></td> */}
                        </tr>
                        ))}
                        </tbody>
                    </table>
                    </div>
                    {/* Pagination */}
                </Card>
            </div>


        </div>
    );
};

export default CustomerHome;
